import { Button, Col, Divider, Icons, message, PageHeader, Row, Spin, Tag } from '@pankod/refine-antd'
import { useNavigation, useOne, useUpdate } from '@pankod/refine-core'
import PopupModal from 'components/PopupModal'
import dayjs from 'dayjs'
import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import Permissions from 'permissions'
import { useUserContext } from 'context/user'

const { LeftOutlined, ClockCircleOutlined, CheckOutlined } = Icons

type Permission = {
  granted?: boolean
}

export const SupportTaskShow: React.FC = () => {
  const { userRole } = useUserContext()
  const { goBack, push } = useNavigation()
  const params: any = useParams()
  const pathname = window.location.pathname
  const splitStr = pathname?.split('/')
  const task_id = splitStr?.slice(4)?.[0]
  const { mutateAsync } = useUpdate()
  const [open, setOpen] = useState<boolean>(false)
  const [readPermission, setReadPermission] = useState<Permission>({})
  const { isLoading, data } = useOne({
    resource: 'support_tasks',
    id: task_id,
    metaData: {
      fields: [
        'id',
        'uid',
        'active',
        'name',
        'assignee_id',
        'description',
        { assignee_user_details: ['displayName'] },
        'created_by',
        { created_by_user_details: ['displayName'] },
        'start_date',
        'end_date',
        'status',
        'module_id',
        { module: ['id', 'label'] },
        {
          permissions: ['id', 'active', 'can_create', 'can_read', 'can_update', 'can_delete', 'module_id', { module: ['id', 'label'] }],
        },
      ],
    },
    queryOptions: {
      enabled: !!task_id,
    },
  })
  const task = data?.data
  const access = task?.permissions?.[0]
  const start = dayjs(task?.start_date)
  const end = dayjs(task?.end_date)
  const days = end.diff(start, 'days')
  const modules = task?.permissions
  const handleConfirm = async () => {
    await Promise.all([
      mutateAsync({
        resource: 'permissions',
        id: access?.id,
        values: {
          active: false,
        },
        successNotification: false,
      }),
      mutateAsync({
        resource: 'support_tasks',
        id: task_id,
        values: {
          active: false,
          status: 'Retired',
        },
        successNotification: false,
      }),
    ])
      .then(() => {
        message.open({
          content: 'Permissions Reverted Successfully',
          type: 'success',
        })
        setOpen(false)
      })
      .catch(() => {
        message.open({
          content: 'Something went wrong !',
          type: 'error',
        })
        setOpen(false)
      })
  }
  const handleComplete = async () => {
    mutateAsync(
      {
        resource: 'permissions',
        id: access?.id,
        values: {
          active: false,
        },
        successNotification: false,
      },
      {
        onSuccess: () => {
          message.open({
            content: 'Permissions Reverted Successfully',
            type: 'success',
          })
          setOpen(false)
        },
      }
    )
  }
  useEffect(() => {
    if (!readPermission?.granted && readPermission?.granted !== undefined) push('/')
    // console.log(readPermission, 'readpermission')
  }, [readPermission])
  useEffect(() => {
    if (userRole !== undefined) {
      setReadPermission(Permissions.can(userRole).readAny('support_tasks'))
      // setCreatePermission(Permissions.can(userRole).createAny('support_tasks'))
      // setEditPermission(Permissions.can(userRole).updateAny('support_tasks'))
      // setDeletePermission(Permissions.can(userRole).deleteAny('support_tasks'))
    }
  }, [userRole])
  // if (isLoading) {
  //   return <Spin />
  // }
  return (
    <PageHeader
      className="site-page-header-responsive"
      ghost={false}
      title={`#${task?.uid} - ${task?.name}`}
      extra={[
        <Button key="close" onClick={() => goBack()} icon={<LeftOutlined />}>
          Back
        </Button>,
        <>
          {/* try hasura events when task is completed */}
          <Button
            type="ghost"
            danger
            icon={null}
            disabled={task?.status === 'Retired' || task?.status === 'Expired' || (task?.status === 'Completed' && access?.active === false)}
            onClick={() => setOpen(true)}
          >
            Revert Permissions
          </Button>
          <PopupModal
            visible={open}
            close={() => {
              setOpen(false)
            }}
            title="Do you want to revert the permissions ?"
            confirm={task?.status === 'Completed' ? handleComplete : handleConfirm}
          />
        </>,
      ]}
    >
      <Row
        gutter={16}
        style={{
          marginBottom: '1rem',
        }}
      >
        <Col lg={24} xs={24}>
          <Tag
            color={
              task?.status === 'Assigned'
                ? 'cyan'
                : task?.status === 'Completed'
                ? 'green'
                : task?.status === 'In Progress'
                ? 'blue'
                : task?.status === 'Expired'
                ? 'volcano'
                : 'geekblue'
            }
          >
            {task?.status}
          </Tag>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col xs={24} lg={6}>
          <h4
            style={{
              fontWeight: 'bold',
              marginBottom: '2px',
            }}
          >
            Assignee:
          </h4>
          <p
            style={{
              textTransform: 'capitalize',
            }}
          >
            {task?.assignee_user_details?.displayName}
          </p>
        </Col>
        <Col xs={24} lg={6}>
          <h4
            style={{
              fontWeight: 'bold',
              marginBottom: '2px',
            }}
          >
            Assigned by:
          </h4>
          <p
            style={{
              textTransform: 'capitalize',
            }}
          >
            {task?.created_by_user_details?.displayName}
          </p>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col xs={24} lg={6}>
          <h4
            style={{
              fontWeight: 'bold',
              marginBottom: '2px',
            }}
          >
            Start Date:
          </h4>
          <p
            style={{
              textTransform: 'capitalize',
            }}
          >
            {task?.start_date}
          </p>
        </Col>
        <Col xs={24} lg={6}>
          <h4
            style={{
              fontWeight: 'bold',
              marginBottom: '2px',
            }}
          >
            End Date:
          </h4>
          <p
            style={{
              textTransform: 'capitalize',
            }}
          >
            {task?.end_date}
          </p>
        </Col>
        <Col xs={24} lg={6}>
          <h4
            style={{
              fontWeight: 'bold',
              marginBottom: '2px',
            }}
          >
            No of Days:
          </h4>
          <p
            style={{
              textTransform: 'capitalize',
            }}
          >
            {days}
          </p>
        </Col>
      </Row>
      <Row>
        <Col lg={24} xs={24}>
          <h4
            style={{
              fontWeight: 'bold',
              marginBottom: '2px',
            }}
          >
            Task Description:
          </h4>
          <p
            style={{
              textTransform: 'capitalize',
            }}
          >
            {task?.description}
          </p>
        </Col>
      </Row>
      <Divider style={{ margin: '12px 0' }} />
      <Row gutter={16}>
        <Col xs={24} lg={6}>
          <h4
            style={{
              fontWeight: 'bold',
              marginBottom: '2px',
            }}
          >
            Module Assigned:
          </h4>
          {modules?.map((module: any) => (
            <Tag
              style={{
                marginBottom: '8px',
              }}
            >
              {module?.module?.label}
            </Tag>
          ))}
        </Col>
        <Col lg={18} xs={24}>
          {console.log('fix_modules', modules)}
          <h4
            style={{
              fontWeight: 'bold',
              marginBottom: '2px',
            }}
          >
            Module Permissions:
          </h4>

          <Row>
            {modules?.map((module: any) => (
              <Col
                lg={4}
                style={{
                  marginBottom: '8px',
                }}
              >
                <h5
                  style={{
                    marginBottom: '8px',
                  }}
                >
                  {module?.module?.label}
                </h5>
                {module?.can_create && <Tag color="green">Create</Tag>}
                {module?.can_read && <Tag color="green">View</Tag>}
                {module?.can_update && <Tag color="green">Edit</Tag>}
                {module?.can_delete && <Tag color="green">Delete</Tag>}
              </Col>
            ))}
          </Row>
        </Col>
      </Row>
    </PageHeader>
  )
}
